/* @ngInject */

export default (
  $q,
  $window,
  $http,
  $state,
  $rootScope,
  $localStorage,
  $timeout,
  $filter,
  CacheFactory,
  Idle,
  jwtHelper,
  logger,
  eventService,
  Organization,
  Account,
  TimeUnit,
  AUTHENTICATION
) => {
  /**
   * Check user credentials.
   *
   * @param {Object} [_params]
   * @param {String} [_params.email] - User E-Mail.
   * @param {String} [_params.password] - User Password.
   * @param {String} [_params.totp] - User TOTP.
   *
   * @returns {Promise}
   */
  function credentialsCheck(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.email || !_params.password) {
      logger.error(
        'authenticationDataFactory',
        'credentialsCheck',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log('authenticationDataFactory', 'credentialsCheck', 'Resolving data from API');
      $http.post(AUTHENTICATION.API.CREDENTIALS_CHECK, _params).then(
        function (payload) {
          if (payload && payload.data && payload.status === 200) {
            logger.debug(
              'authenticationDataFactory',
              'credentialsCheck',
              'Resolved data from API'
            );
            if (payload.data && payload.data.twoFactor === false) {
              if (payload.data.twoFactorSMS) {
                deferred.resolve('twoFactor:required:isSMS');
              } else {
                deferred.resolve('twoFactor:required');
              }
            } else {
              credentialsSet({ tokens: payload.data }).then(function () {
                credentialsValidate().then(function (payload) {
                  deferred.resolve(payload);
                });
              });
            }
          } else {
            logger.error(
              'authenticationDataFactory',
              'credentialsCheck',
              'Error while resolving data',
              'Erroneous API response'
            );
            credentialsClear().then(function () {
              deferred.resolve(false);
            });
          }
        },
        function (err) {
          logger.error(
            'authenticationDataFactory',
            'credentialsCheck',
            'Error while resolving data',
            err
          );
          deferred.resolve(false);
        }
      );
    }
    return deferred.promise;
  }

  /**
   * Clear user credentials.
   *
   * @returns {Promise}
   */
  function credentialsClear() {
    var deferred = $q.defer();

    CacheFactory.clearAll();

    delete $localStorage.masterToken;
    delete $localStorage.authenticationToken;
    delete $rootScope.CurrentAccount;
    delete $rootScope.AvailableOrganizations;
    delete $rootScope.CurrentOrganization;
    delete $rootScope.CurrentTimeZone;
    delete $rootScope.CurrentLocale;
    delete $rootScope.CurrentDateFormat;
    delete $rootScope.CurrentDateFormatPrimary;
    delete $rootScope.CurrentDateFormatSecondary;
    delete $rootScope.CurrentTimeFormat;
    delete $rootScope.CurrentTimeUnit;
    delete $rootScope.CurrentCurrency;

    Idle.unwatch();

    $window.Intercom('shutdown');
    $window.Upscope('updateConnection', { uniqueId: undefined, identities: [undefined] });
    eventService.credentialsChanged();
    eventService.intercomHide();

    deferred.resolve(true);

    return deferred.promise;
  }

  /**
   * Set user credentials.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.tokens] - User Credentials Tokens.
   *
   * @returns {Promise}
   */
  function credentialsSet(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.tokens) {
      logger.error(
        'authenticationDataFactory',
        'credentialsSet',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log('authenticationDataFactory', 'credentialsSet', 'Setting Tokens');
      $localStorage.masterToken = _params.tokens.masterToken;
      $localStorage.authenticationToken = _params.tokens.authToken;
      deferred.resolve(true);
    }
    return deferred.promise;
  }

  /**
   * Validate set user credentials.
   *
   * @returns {Promise}
   */
  function credentialsValidate() {
    var deferred = $q.defer();
    if (!isTokenValid()) {
      logger.log(
        'authenticationDataFactory',
        'credentialsValidate',
        'Clearing user credentials'
      );
      credentialsClear().then(function (payload) {
        logger.debug(
          'authenticationDataFactory',
          'credentialsValidate',
          'Cleared user credentials'
        );
        deferred.resolve(!payload);
      });
    } else {
      logger.log(
        'authenticationDataFactory',
        'credentialsValidate',
        'Validating user credentials'
      );
      try {
        var tokenData = jwtHelper.decodeToken($localStorage.masterToken);

        $rootScope.CurrentAccount = Account.build(tokenData);
        $rootScope.CurrentAccount.isAdministrator = tokenData.isAdministrator;
        $rootScope.CurrentAccount.isBroker = tokenData.isBroker;
        $rootScope.CurrentAccount.isMentor = tokenData.isMentor;
        $rootScope.AvailableOrganizations = $filter('orderBy')(
          _.map(tokenData.organizations, function (organization) {
            return Organization.build(organization);
          }),
          'GetName()'
        );
        $rootScope.CurrentOrganization = Organization.build(tokenData.organization);
        $rootScope.CurrentTimeZone = function () {
          return $rootScope.CurrentOrganization.organization.timezone &&
            $rootScope.CurrentOrganization.organization.timezone !== ''
            ? $rootScope.CurrentOrganization.organization.timezone
            : 'UTC';
        };
        $rootScope.CurrentLocale = function () {
          return 'en-AU';
        };
        $rootScope.CurrentDateFormat = function () {
          return $rootScope.CurrentOrganization.organization.dateFormat;
        };
        $rootScope.CurrentDateFormatPrimary = function () {
          return 'DD/MM/YYYY';
        };
        $rootScope.CurrentDateFormatSecondary = function () {
          return 'MM/YYYY';
        };
        $rootScope.CurrentTimeFormat = function () {
          return $rootScope.CurrentOrganization.organization.timeFormat;
        };
        $rootScope.CurrentTimeUnit = function () {
          return $rootScope.CurrentOrganization.organization.timeUnit
            ? $rootScope.CurrentOrganization.organization.timeUnit
            : TimeUnit.Build('');
        };
        $rootScope.CurrentCurrency = function () {
          const defaultCurrency = {
            cc: 'AUD',
            name: 'Australia dollar',
            symbol: '$'
          };
          return $rootScope.CurrentOrganization.organization.currency || defaultCurrency;
        };

        if (!Idle.running()) {
          Idle.watch();
        }

        logger.log(
          'authenticationDataFactory',
          'credentialsValidate',
          'Credentials will expire in',
          tokenGetExpirationFromNow(0, true).toString().formatAsDuration()
        );
        $timeout(function () {
          eventService.credentialsExpiring();
        }, tokenGetExpirationFromNow(5));

        $timeout(function () {
          $state.transitionTo('authenticate');
        }, tokenGetExpirationFromNow());

        $window.Intercom('boot', {
          app_id: 'ratt5ci6',
          user_hash: tokenData.intercom,
          id: $rootScope.CurrentAccount.id,
          name: $rootScope.CurrentAccount.getName(),
          email: $rootScope.CurrentAccount.getEmail(),
          phone: $rootScope.CurrentAccount.getPhone(),
          avatar: {
            image_url: $rootScope.CurrentAccount.getAvatar()
          },
          created_at: $rootScope.CurrentAccount.timeStamp,
          company: {
            id: $rootScope.CurrentOrganization.id,
            name: $rootScope.CurrentOrganization.getName(),
            plan: $rootScope.CurrentOrganization.isSubscribed()
              ? $rootScope.CurrentOrganization.getSubscriptionPlanId()
              : 'TRIAL',
            created_at: $rootScope.CurrentOrganization.timeStamp
          }
        });
        $window.Upscope('updateConnection', {
          uniqueId: $rootScope.CurrentAccount.id,
          identities: [
            $rootScope.CurrentAccount.getName(),
            $rootScope.CurrentAccount.getEmail()
          ]
        });

        eventService.credentialsChanged();

        logger.debug(
          'authenticationDataFactory',
          'credentialsValidate',
          'Validated user credentials'
        );
        deferred.resolve(true);
      } catch (err) {
        credentialsClear().then(function (payload) {
          logger.error(
            'authenticationDataFactory',
            'credentialsValidate',
            'Cleared user credentials',
            err
          );
          deferred.resolve(!payload);
        });
      }
    }

    return deferred.promise;
  }

  /**
   * Check if current user is authenticated.
   *
   * @returns {Boolean}
   */
  function isAuthenticated() {
    return isTokenValid();
  }

  /**
   * Check is user authentication token is expired.
   *
   * @returns {Boolean}
   */
  function isTokenExpired() {
    return jwtHelper.isTokenExpired(tokenGet());
  }

  /**
   * Check is user authentication token is set.
   *
   * @returns {Boolean}
   */
  function isTokenPresent() {
    return tokenGet() !== undefined;
  }

  /**
   * Check is user authentication token is set and not-expired.
   *
   * @returns {Boolean}
   */
  function isTokenValid() {
    return isTokenPresent() && !isTokenExpired();
  }

  /**
   * Change organization for user.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.idOrganization] - Organization ID.
   *
   * @returns {Promise}
   */
  function organizationChange(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.idOrganization) {
      logger.error(
        'authenticationDataFactory',
        'organizationChange',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log('authenticationDataFactory', 'organizationChange', 'Resolving data from API');
      $http.post(AUTHENTICATION.API.ORGANIZATION_CHANGE, _params).then(
        function (payload) {
          if (!payload || !payload.data) {
            logger.error(
              'authenticationDataFactory',
              'organizationChange',
              'Error while resolving data',
              'Erroneous API response'
            );
            deferred.reject();
          }
          return credentialsClear()
            .then(function () {
              return credentialsSet({ tokens: payload.data });
            })
            .then(function (status) {
              if (status) {
                return credentialsValidate();
              } else {
                return false;
              }
            })
            .then(function (status) {
              deferred.resolve(status);
            });
        },
        function (err) {
          logger.error(
            'authenticationDataFactory',
            'organizationChange',
            'Error while resolving data',
            err
          );
          deferred.resolve(false);
        }
      );
    }
    return deferred.promise;
  }

  /**
   * Initialize Password Reset procedure for user.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.email] - User E-Mail.
   *
   * @returns {Promise}
   */
  function passwordForgot(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.email) {
      logger.error(
        'authenticationDataFactory',
        'passwordForgot',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log('authenticationDataFactory', 'passwordForgot', 'Resolving data from API');
      $http.patch(AUTHENTICATION.API.PASSWORD_FORGOT, _params).then(
        function (payload) {
          if (!payload || !payload.data) {
            logger.error(
              'authenticationDataFactory',
              'passwordForgot',
              'Error while resolving data',
              'Erroneous API response'
            );
            deferred.reject();
          }
          logger.debug('authenticationDataFactory', 'passwordForgot', 'Resolved data from API');
          deferred.resolve(payload.data.status);
        },
        function (err) {
          logger.error(
            'authenticationDataFactory',
            'passwordForgot',
            'Error while resolving data',
            err
          );
          deferred.resolve(false);
        }
      );
    }
    return deferred.promise;
  }

  /**
   * Reset Password for User.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.oldPassword] - User current Password.
   * @param {Boolean} [_params.newPassword] - User new Password.
   *
   * @returns {Promise}
   */
  function passwordChange(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.oldPassword || !_params.newPassword) {
      logger.error(
        'authenticationDataFactory',
        'passwordChange',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log('authenticationDataFactory', 'passwordChange', 'Resolving data from API');
      $http.patch(AUTHENTICATION.API.PASSWORD_CHANGE, _params).then(
        function (payload) {
          if (!payload || !payload.data) {
            logger.error(
              'authenticationDataFactory',
              'passwordChange',
              'Error while resolving data',
              'Erroneous API response'
            );
            deferred.resolve(false);
          }
          logger.debug('authenticationDataFactory', 'passwordChange', 'Resolved data from API');
          deferred.resolve(payload.data.status);
        },
        function (err) {
          logger.error(
            'authenticationDataFactory',
            'passwordChange',
            'Error while resolving data',
            err
          );
          deferred.resolve(false);
        }
      );
    }
    return deferred.promise;
  }

  /**
   * Retrieve user authentication token.
   *
   * @returns {Object}
   */
  function tokenGet() {
    return $localStorage.authenticationToken;
  }

  /**
   * Retrieve authentication token expiration date.
   *
   * @returns {Date}
   */
  function tokenGetExpiration() {
    return jwtHelper.getTokenExpirationDate(tokenGet());
  }

  /**
   * Retrieve authentication token expiration date from now, in milliseconds.
   *
   * @param {Number} _minutesBeforeExpiration - Shorten the expiration date for this amount of minutes.
   * @param {Boolean} [_convertToSeconds] - Return the value as seconds instead of milliseconds.
   *
   * @returns {Number}
   */
  function tokenGetExpirationFromNow(_minutesBeforeExpiration, _convertToSeconds) {
    var now = moment.utc();
    var expirationDate = tokenGetExpiration();
    var expirationDateInUtc = moment(expirationDate).tz('UTC');
    var durationInMilliseconds = moment
      .duration(expirationDateInUtc.diff(now))
      .asMilliseconds();
    var expiresIn = !_minutesBeforeExpiration
      ? durationInMilliseconds
      : durationInMilliseconds - _minutesBeforeExpiration * 60 * 1000;
    return expiresIn / (_convertToSeconds ? 1000 : 1);
  }

  /**
   * Initialize Lost Two-Factor device procedure for User.
   *
   * @param {Object} [_params]
   * @param {String} [_params.email] - User E-Mail.
   * @param {String} [_params.password] - User password.
   * @param {String} [_params.phone] - User partial phone number.
   *
   * @returns {Promise}
   */
  function twoFactorLost(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.phone) {
      logger.error(
        'authenticationDataFactory',
        'twoFactorLost',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log('authenticationDataFactory', 'twoFactorLost', 'Resolving data from API');
      $http.post(AUTHENTICATION.API.TWO_FACTOR_LOST, _params).then(
        function (payload) {
          if (!payload || !payload.data) {
            logger.error(
              'authenticationDataFactory',
              'twoFactorLost',
              'Error while resolving data',
              'Erroneous API response'
            );
            deferred.reject();
          }
          logger.debug('authenticationDataFactory', 'twoFactorLost', 'Resolved data from API');
          deferred.resolve(payload.data.status);
        },
        function (err) {
          logger.error(
            'authenticationDataFactory',
            'twoFactorLost',
            'Error while resolving data',
            err
          );
          deferred.resolve(false);
        }
      );
    }
    return deferred.promise;
  }

  /**
   * Toggle TOTP for User.
   *
   * @returns {Promise}
   */
  function twoFactorToggle() {
    var deferred = $q.defer();

    logger.log('authenticationDataFactory', 'twoFactorToggle', 'Resolving data from API');
    $http.post(AUTHENTICATION.API.TWO_FACTOR_TOGGLE).then(
      function (payload) {
        if (!payload || !payload.data) {
          logger.error(
            'authenticationDataFactory',
            'twoFactorToggle',
            'Error while resolving data',
            'Erroneous API response'
          );
          deferred.reject();
        }
        deferred.resolve(payload.data.status);
      },
      function (err) {
        logger.error(
          'authenticationDataFactory',
          'twoFactorToggle',
          'Error while resolving data',
          err
        );
        deferred.resolve(false);
      }
    );

    return deferred.promise;
  }

  /**
   * Send SMS Code.
   *
   * @returns {Promise}
   */
  function twoFactorSMS() {
    var deferred = $q.defer();

    logger.log('authenticationDataFactory', 'twoFactorSMS', 'Resolving data from API');
    $http.post(AUTHENTICATION.API.TWO_FACTOR_SMS).then(
      function (payload) {
        if (!payload || !payload.data) {
          logger.error(
            'authenticationDataFactory',
            'twoFactorSMS',
            'Error while resolving data',
            'Erroneous API response'
          );
          deferred.reject();
        }
        deferred.resolve(payload.data.status);
      },
      function (err) {
        logger.error(
          'authenticationDataFactory',
          'twoFactorSMS',
          'Error while resolving data',
          err
        );
        deferred.resolve(false);
      }
    );

    return deferred.promise;
  }

  /**
   * Validate SMS for User.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.totp] - User current TOTP.
   *
   * @returns {Promise}
   */
  function twoFactorSMSValidate(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.totp) {
      logger.error(
        'authenticationDataFactory',
        'twoFactorSMSValidate',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log(
        'authenticationDataFactory',
        'twoFactorSMSValidate',
        'Resolving data from API'
      );
      $http.post(AUTHENTICATION.API.TWO_FACTOR_SMS_VALIDATE, _params).then(
        function (payload) {
          if (!payload || !payload.data) {
            logger.error(
              'authenticationDataFactory',
              'twoFactorSMSValidate',
              'Error while resolving data',
              'Erroneous API response'
            );
            deferred.reject();
          }
          deferred.resolve(payload.data.status);
        },
        function (err) {
          logger.error(
            'authenticationDataFactory',
            'twoFactorSMSValidate',
            'Error while resolving data',
            err
          );
          deferred.resolve(false);
        }
      );
    }
    return deferred.promise;
  }

  /**
   * Validate TOTP for User.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.totp] - User current TOTP.
   *
   * @returns {Promise}
   */
  function twoFactorValidate(_params) {
    var deferred = $q.defer();
    if (!_params || !_params.totp) {
      logger.error(
        'authenticationDataFactory',
        'twoFactorValidate',
        'Not all required parameters are present'
      );
      deferred.reject();
    } else {
      logger.log('authenticationDataFactory', 'twoFactorValidate', 'Resolving data from API');
      $http.post(AUTHENTICATION.API.TWO_FACTOR_VALIDATE, _params).then(
        function (payload) {
          if (!payload || !payload.data) {
            logger.error(
              'authenticationDataFactory',
              'twoFactorValidate',
              'Error while resolving data',
              'Erroneous API response'
            );
            deferred.reject();
          }
          deferred.resolve(payload.data.status);
        },
        function (err) {
          logger.error(
            'authenticationDataFactory',
            'twoFactorValidate',
            'Error while resolving data',
            err
          );
          deferred.resolve(false);
        }
      );
    }
    return deferred.promise;
  }

  var service = {
    credentialsCheck: credentialsCheck,
    credentialsClear: credentialsClear,
    credentialsSet: credentialsSet,
    credentialsValidate: credentialsValidate,
    isAuthenticated: isAuthenticated,
    organizationChange: organizationChange,
    passwordForgot: passwordForgot,
    passwordChange: passwordChange,
    tokenGet: tokenGet,
    tokenGetExpiration: tokenGetExpiration,
    tokenGetExpirationFromNow: tokenGetExpirationFromNow,
    twoFactorLost: twoFactorLost,
    twoFactorSMS: twoFactorSMS,
    twoFactorSMSValidate: twoFactorSMSValidate,
    twoFactorToggle: twoFactorToggle,
    twoFactorValidate: twoFactorValidate
  };
  return service;
};
